import {
  IAzureBlobUploadInitDto,
  IInitializeAzureBlobUploadDto,
  IInitializeROVVideoDto,
  IROVVideoCategory,
  IROVVideosWithSharedCategories,
} from 'src/generated-types/rovvideo'
import { ResumableBlobUpload, uploadFileToBlob } from 'src/utils/azureBlobUpload'
import { deriveScreenshotFileName } from 'src/utils/rovVideoScreenshot'
import {
  buildCreateBlobVideoPayload,
  buildCreateVimeoVideoPayload,
  CreateBlobVideoPayload,
  CreateVimeoVideoPayload,
  RovVideoFormValues,
  RovVideoUploadTargetIds,
} from 'src/utils/rovVideoUploadPayload'
import { extractVimeoId, ResumableVimeoUpload } from 'src/utils/vimeoUpload'

export { buildCreateBlobVideoPayload, buildCreateVimeoVideoPayload }
export type { CreateBlobVideoPayload, CreateVimeoVideoPayload, RovVideoFormValues, RovVideoUploadTargetIds }

export interface SubmitAzureUploadParams {
  formData: RovVideoFormValues
  file: File
  ids: RovVideoUploadTargetIds
  screenshotPromise: Promise<Blob | null> | null
  initializeBlobUpload: (input: IInitializeAzureBlobUploadDto) => Promise<{ data: IAzureBlobUploadInitDto }>
  createROVVideo: (input: CreateBlobVideoPayload) => Promise<unknown>
  resumableUploadRef: React.MutableRefObject<ResumableBlobUpload | ResumableVimeoUpload | null>
  onProgress: (loaded: number, total: number) => void
  onScreenshotUploadFailed?: () => void
}

export const submitAzureUpload = async (params: SubmitAzureUploadParams): Promise<void> => {
  const {
    formData,
    file,
    ids,
    screenshotPromise,
    initializeBlobUpload,
    createROVVideo,
    resumableUploadRef,
    onProgress,
    onScreenshotUploadFailed,
  } = params

  const screenshotBlob = await screenshotPromise

  const initResponse = await initializeBlobUpload({
    fileSize: file.size,
    fileName: file.name,
    screenshotFileName: screenshotBlob ? deriveScreenshotFileName(file.name) : undefined,
    title: formData.title,
    description: formData.description,
  })
  const responseData = initResponse.data

  if (!responseData?.uploadUrl) {
    throw new Error('Failed to initialize blob upload')
  }

  resumableUploadRef.current = new ResumableBlobUpload(file, responseData.uploadUrl, { onProgress })

  const screenshotUploadPromise =
    screenshotBlob && responseData.screenshotUploadUrl
      ? uploadFileToBlob(screenshotBlob, responseData.screenshotUploadUrl)
      : Promise.resolve()

  const [videoResult, screenshotResult] = await Promise.allSettled([
    resumableUploadRef.current.start(),
    screenshotUploadPromise,
  ])

  if (videoResult.status === 'rejected') {
    throw videoResult.reason instanceof Error ? videoResult.reason : new Error('Video upload failed')
  }

  const screenshotUploadSucceeded = screenshotResult.status === 'fulfilled'
  if (screenshotBlob && !screenshotUploadSucceeded) {
    console.warn('Screenshot upload failed:', (screenshotResult as PromiseRejectedResult).reason)
    onScreenshotUploadFailed?.()
  }

  await createROVVideo(buildCreateBlobVideoPayload(formData, responseData, screenshotUploadSucceeded, file.size, ids))
}

export interface SubmitVimeoUploadParams {
  formData: RovVideoFormValues
  file: File
  ids: RovVideoUploadTargetIds
  createVimeoPlaceholder: (input: IInitializeROVVideoDto) => Promise<{ data: any }>
  createROVVideo: (input: CreateVimeoVideoPayload) => Promise<unknown>
  resumableUploadRef: React.MutableRefObject<ResumableBlobUpload | ResumableVimeoUpload | null>
  onProgress: (loaded: number, total: number) => void
}

export const submitVimeoUpload = async (params: SubmitVimeoUploadParams): Promise<void> => {
  const { formData, file, ids, createVimeoPlaceholder, createROVVideo, resumableUploadRef, onProgress } = params

  const placeholderResponse = await createVimeoPlaceholder({
    fileSize: file.size,
    title: formData.title,
    description: formData.description,
  })
  const responseData = placeholderResponse.data

  if (!responseData?.upload?.upload_link) {
    throw new Error('Failed to initialize Vimeo upload')
  }

  resumableUploadRef.current = new ResumableVimeoUpload(file, responseData.upload.upload_link, { onProgress })
  await resumableUploadRef.current.start()

  const vimeoId = extractVimeoId(responseData.player_embed_url, process.env.REACT_APP_VIMEO_URL)

  await createROVVideo(buildCreateVimeoVideoPayload(formData, vimeoId, ids))
}

export function getUniqueCategories(
  rovVideosWithSharedCategories?: IROVVideosWithSharedCategories,
): IROVVideoCategory[] {
  const categoryMap = new Map<EntityId, IROVVideoCategory>()

  if (rovVideosWithSharedCategories?.videos) {
    rovVideosWithSharedCategories.videos.forEach(video => {
      if (video.category?.id && video.category?.categoryName?.trim()) {
        categoryMap.set(video.category.id, {
          id: video.category.id,
          categoryName: video.category.categoryName.trim(),
          isShared: video.category.isShared,
        })
      }
    })
  }

  if (rovVideosWithSharedCategories?.sharedCategories) {
    rovVideosWithSharedCategories.sharedCategories.forEach(category => {
      if (category.id && category.categoryName?.trim()) {
        categoryMap.set(category.id, {
          id: category.id,
          categoryName: category.categoryName.trim(),
          isShared: category.isShared,
        })
      }
    })
  }

  return Array.from(categoryMap.values()).sort((a, b) => a.categoryName.localeCompare(b.categoryName))
}
